import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Building2, Copy, Link2, ExternalLink, Plus, QrCode, Trash2, History, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

export default function BodyShopPortal() {
  const [contacts, setContacts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrShop, setQrShop] = useState(null);
  const [auditShop, setAuditShop] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [slugDraft, setSlugDraft] = useState("");

  const load = () =>
    Promise.all([
      api.get("/crm/contacts?type=body_shop"),
      api.get("/jobs"),
      api.get("/invoices?type=body_shop"),
    ]).then(([c, j, i]) => {
      setContacts(c.data); setJobs(j.data); setInvoices(i.data); setLoading(false);
    });

  useEffect(() => { load(); }, []);

  const generateToken = async (id) => {
    try {
      await api.post(`/crm/contacts/${id}/generate-token`);
      toast.success("Public link generated");
      load();
    } catch (e) {
      toast.error("Failed to generate token");
    }
  };

  const revokeToken = async (id, name) => {
    if (!window.confirm(`Revoke public link for ${name}? The old URL will stop working.`)) return;
    try {
      await api.delete(`/crm/contacts/${id}/token`);
      toast.success("Public link revoked");
      load();
    } catch (e) {
      toast.error("Failed to revoke");
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/submit/${token}`);
    toast.success("Public link copied");
  };

  const openAudit = async (shop) => {
    setAuditShop(shop);
    try {
      const r = await api.get(`/crm/contacts/${shop.id}/audit`);
      setAuditLogs(r.data);
    } catch (e) { setAuditLogs([]); }
  };

  const startEditSlug = (shop) => {
    setEditingSlug(shop.id);
    setSlugDraft(shop.slug || shop.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""));
  };

  const saveSlug = async (shop) => {
    try {
      await api.patch(`/crm/contacts/${shop.id}/slug`, { slug: slugDraft });
      toast.success(`Custom URL set: /shop/${slugDraft}`);
      setEditingSlug(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const shopStats = contacts.map((c) => {
    const shopJobs = jobs.filter((j) => j.body_shop_name === c.name);
    const shopInvoices = invoices.filter((i) => i.recipient_name === c.name);
    const totalCommission = shopInvoices.reduce((s, i) => s + i.total, 0);
    const publicSubs = shopJobs.filter((j) => j.submitted_via_public).length;
    return { ...c, jobs: shopJobs.length, totalCommission, paid: shopInvoices.filter((i) => i.status === "paid").length, publicSubs };
  });

  return (
    <div className="space-y-6 animate-slide-up" data-testid="body-shop-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Body Shop Network</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Body Shop Portal</h1>
        <p className="text-sm text-slate-400 mt-1.5">Partner shops + their unique public submission links and QR codes.</p>
      </div>

      <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-5">
        <div className="flex items-start gap-3">
          <Link2 className="w-5 h-5 text-cyan-400 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-cyan-300 mb-1">Public submission links + QR codes</div>
            <div className="text-slate-300 text-xs leading-relaxed">
              Each body shop has a unique URL like <span className="font-mono text-blue-300">/submit/&lt;token&gt;</span> and a scannable QR code. Submissions are rate-limited to 5/min/IP, and each creates a magic-link for the partner to track their job.
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 text-center py-12">Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {shopStats.map((s) => {
            const publicUrl = s.submission_token ? `${window.location.origin}/submit/${s.submission_token}` : null;
            return (
              <div key={s.id} className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 hover:border-blue-500/40 transition-colors" data-testid={`shop-card-${s.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/15 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total Commission</div>
                    <div className="font-mono text-cyan-400">${s.totalCommission.toLocaleString()}</div>
                  </div>
                </div>
                <div className="font-display text-lg font-medium">{s.name}</div>
                {s.address && <div className="text-xs text-slate-500 mt-1">{s.address}</div>}

                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-800/60">
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Jobs</div><div className="text-sm font-mono text-slate-200">{s.jobs}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Paid</div><div className="text-sm font-mono text-emerald-400">{s.paid}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Via Link</div><div className="text-sm font-mono text-cyan-400">{s.publicSubs}</div></div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-800/60">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2 flex items-center gap-1.5">
                    <Link2 className="w-3 h-3" /> Public link
                  </div>
                  {publicUrl ? (
                    <>
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/60 border border-slate-800">
                        <input data-testid={`public-link-${s.id}`} readOnly value={publicUrl} onClick={(e) => e.target.select()}
                          className="flex-1 bg-transparent text-xs font-mono text-blue-300 outline-none truncate" />
                        <button data-testid={`copy-link-${s.id}`} onClick={() => copyLink(s.submission_token)} className="text-slate-400 hover:text-blue-400" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
                        <a data-testid={`open-link-${s.id}`} href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-400" title="Open"><ExternalLink className="w-3.5 h-3.5" /></a>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button data-testid={`qr-btn-${s.id}`} size="sm" variant="outline" className="flex-1 border-slate-700 text-xs" onClick={() => setQrShop(s)}>
                          <QrCode className="w-3.5 h-3.5 mr-1.5" /> QR Code
                        </Button>
                        <Button data-testid={`revoke-btn-${s.id}`} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" onClick={() => revokeToken(s.id, s.name)}>
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Revoke
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button data-testid={`generate-token-${s.id}`} size="sm" variant="outline" onClick={() => generateToken(s.id)}
                      className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 w-full"><Plus className="w-3.5 h-3.5 mr-1.5" />Generate public link</Button>
                  )}
                </div>

                {s.notes && <div className="text-xs text-slate-400 mt-3 leading-relaxed">{s.notes}</div>}

                {/* Custom slug */}
                <div className="mt-3 pt-3 border-t border-slate-800/60">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2 flex items-center gap-1.5">
                    <Tag className="w-3 h-3" /> Memorable URL (slug)
                  </div>
                  {editingSlug === s.id ? (
                    <div className="flex gap-2">
                      <Input value={slugDraft} onChange={(e) => setSlugDraft(e.target.value)} className="bg-[#030712] border-slate-800 text-xs h-8 font-mono" placeholder="premier-auto-body" />
                      <Button size="sm" onClick={() => saveSlug(s)} className="bg-blue-600 hover:bg-blue-500 h-8 text-xs">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingSlug(null)} className="border-slate-700 h-8 text-xs">×</Button>
                    </div>
                  ) : s.slug ? (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-cyan-400">/shop/{s.slug}</span>
                      <button onClick={() => startEditSlug(s)} className="text-slate-400 hover:text-blue-400 ml-auto">Edit</button>
                    </div>
                  ) : (
                    <button onClick={() => startEditSlug(s)} className="text-xs text-slate-400 hover:text-cyan-400">+ Set custom URL</button>
                  )}
                </div>

                {/* Audit history */}
                {(s.token_revoked_at || s.token_revoked_by) && (
                  <div className="mt-3 text-xs text-amber-400/80 flex items-center gap-1.5">
                    <History className="w-3 h-3" />
                    Last revoked by {s.token_revoked_by} ·{" "}
                    <button onClick={() => openAudit(s)} className="underline hover:text-amber-300">view log</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Audit log modal */}
      <Dialog open={!!auditShop} onOpenChange={(o) => !o && setAuditShop(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg" data-testid="audit-modal">
          <DialogHeader><DialogTitle>{auditShop?.name} — Token Audit Log</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {auditLogs.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">No audit events</div>
            ) : auditLogs.map((a) => (
              <div key={a.id} className="p-3 rounded-lg bg-slate-900/50 border border-slate-800/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs uppercase tracking-wider font-bold text-amber-400">{a.action.replace("_", " ")}</span>
                  <span className="text-[10px] font-mono text-slate-500">{new Date(a.at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-slate-300">By: {a.actor_name}</div>
                {a.previous_token && <div className="text-[10px] font-mono text-slate-600 mt-1 truncate">Previous: {a.previous_token}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrShop} onOpenChange={(o) => !o && setQrShop(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md" data-testid="qr-modal">
          <DialogHeader><DialogTitle>{qrShop?.name} — QR Code</DialogTitle></DialogHeader>
          {qrShop && qrShop.submission_token && (
            <div className="space-y-4">
              <div className="rounded-xl bg-white p-6 flex justify-center">
                <QRCodeSVG value={`${window.location.origin}/submit/${qrShop.submission_token}`} size={240} level="M" />
              </div>
              <div className="text-xs text-slate-400 leading-relaxed text-center">
                The body shop can scan this code with any phone camera to instantly open their submission page. Print it on a business card or post in the office.
              </div>
              <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800 font-mono text-xs text-blue-300 break-all">
                {`${window.location.origin}/submit/${qrShop.submission_token}`}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
